import { useState } from 'react';
import { Loader2, Pencil, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import api from '../../services/api';
import { LABEL_COLORS, colorOf } from '../../lib/labels';

const ColorPicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Renk">
    {LABEL_COLORS.map((c) => (
      <button
        key={c.id}
        type="button"
        role="radio"
        aria-checked={value === c.id}
        aria-label={c.name}
        onClick={() => onChange(c.id)}
        className={`h-6 w-6 rounded-full ${c.dot} flex items-center justify-center ring-offset-2 ring-offset-background ${value === c.id ? 'ring-2 ring-foreground/70' : ''}`}
      >
        {value === c.id && <Check className="h-3.5 w-3.5 text-white" />}
      </button>
    ))}
  </div>
);

// Etiket oluştur / yeniden adlandır / renk değiştir / sil
export const LabelDialog = ({ open, onOpenChange, labels, onChanged }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('blue');
  const [editing, setEditing] = useState(null); // { id, name, color }
  const [busy, setBusy] = useState(false);

  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      await fn();
      if (okMessage) toast.success(okMessage);
      await onChanged();
      return true;
    } catch (err) {
      toast.error(err.message || 'İşlem başarısız');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (await run(() => api.createLabel(name, color), 'Etiket oluşturuldu')) setName('');
  };

  const saveEdit = async () => {
    if (await run(() => api.updateLabel(editing.id, editing.name, editing.color), 'Etiket güncellendi')) setEditing(null);
  };

  const remove = async (label) => {
    if (!window.confirm(`"${label.name}" etiketi silinsin mi? Mailler silinmez, yalnızca etiket kalkar.`)) return;
    await run(() => api.deleteLabel(label.id), 'Etiket silindi');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="label-dialog">
        <DialogHeader>
          <DialogTitle>Etiketleri yönet</DialogTitle>
          <DialogDescription>Mailleri etiketlerle düzenleyin; etiketler diğer klasörlere taşınsa da maille birlikte gider.</DialogDescription>
        </DialogHeader>

        <form onSubmit={create} className="space-y-3">
          <div className="flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={50} placeholder="Yeni etiket adı" data-testid="label-name-input" />
            <Button type="submit" disabled={busy || !name.trim()} data-testid="label-create">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ekle'}
            </Button>
          </div>
          <ColorPicker value={color} onChange={setColor} />
        </form>

        <ul className="mt-2 max-h-64 overflow-y-auto divide-y divide-border rounded-lg border border-border" data-testid="label-list">
          {labels.length === 0 && <li className="p-4 text-sm text-muted-foreground text-center">Henüz etiket yok</li>}
          {labels.map((label) => (
            <li key={label.id} className="p-2.5">
              {editing?.id === label.id ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} maxLength={50} autoFocus />
                    <Button size="icon" onClick={saveEdit} disabled={busy || !editing.name.trim()} aria-label="Kaydet"><Check className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setEditing(null)} aria-label="Vazgeç"><X className="h-4 w-4" /></Button>
                  </div>
                  <ColorPicker value={editing.color} onChange={(c) => setEditing({ ...editing, color: c })} />
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className={`h-3 w-3 rounded-full shrink-0 ${colorOf(label.color).dot}`} />
                  <span className="flex-1 truncate text-sm">{label.name}</span>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing({ ...label })} aria-label={`${label.name} düzenle`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(label)} aria-label={`${label.name} sil`} data-testid={`label-delete-${label.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
};

export default LabelDialog;
